import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import {
  DollarSign, Plus, Trash2, TrendingUp, TrendingDown,
  PieChart, Plane, Hotel, Utensils, Ticket, Car, MoreHorizontal,
  Receipt, Target, AlertTriangle, CheckCircle, ArrowRight, Lock, Crown
} from "lucide-react";
import { useEntitlements, Features } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

const EXPENSE_CATEGORIES = [
  { value: "flights", label: "Flights", icon: Plane, color: "bg-blue-500" },
  { value: "accommodation", label: "Accommodation", icon: Hotel, color: "bg-purple-500" },
  { value: "food", label: "Food & Dining", icon: Utensils, color: "bg-orange-500" },
  { value: "activities", label: "Activities", icon: Ticket, color: "bg-green-500" },
  { value: "transport", label: "Transport", icon: Car, color: "bg-yellow-500" },
  { value: "other", label: "Other", icon: MoreHorizontal, color: "bg-gray-500" },
];

export const BudgetTracker = ({ isOpen, onClose, bucketItem }) => {
  const [summary, setSummary] = useState(null);
  const [itemBudget, setItemBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(bucketItem ? "item" : "overview");
  const [showPaywall, setShowPaywall] = useState(false);
  const [isPremiumLocked, setIsPremiumLocked] = useState(false);
  
  const { hasPaidAccess } = useEntitlements();
  
  // Expense form
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    category: "activities",
    amount: "",
    description: "",
    date: format(new Date(), "yyyy-MM-dd")
  });

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/budget/summary`);
      setSummary(res.data);
      setIsPremiumLocked(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsPremiumLocked(true);
      } else {
        console.error("Failed to fetch budget summary");
      }
    }
  };

  const fetchItemBudget = async () => {
    if (!bucketItem) return;
    try {
      const res = await axios.get(`${API}/budget/${bucketItem.id}`);
      setItemBudget(res.data);
    } catch (error) {
      console.error("Failed to fetch item budget");
    }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    if (!bucketItem || !expenseForm.amount) {
      toast.error("Please enter an amount");
      return;
    }
    
    try {
      await axios.post(`${API}/budget/${bucketItem.id}/expense`, {
        category: expenseForm.category,
        amount: parseFloat(expenseForm.amount),
        description: expenseForm.description,
        date: expenseForm.date
      });
      
      toast.success("Expense added!");
      setShowAddExpense(false);
      setExpenseForm({ category: "activities", amount: "", description: "", date: format(new Date(), "yyyy-MM-dd") });
      fetchItemBudget();
      fetchSummary();
    } catch (error) {
      toast.error("Failed to add expense");
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!bucketItem) return;
    
    try {
      await axios.delete(`${API}/budget/${bucketItem.id}/expense/${expenseId}`);
      toast.success("Expense deleted");
      fetchItemBudget();
      fetchSummary();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([fetchSummary(), fetchItemBudget()]).finally(() => setLoading(false));
      if (bucketItem) {
        setActiveTab("item");
      }
    }
  }, [isOpen, bucketItem]);

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return "text-destructive";
    if (variance < 0) return "text-secondary";
    return "text-muted-foreground";
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <TrendingUp className="w-4 h-4 text-destructive" />;
    if (variance < 0) return <TrendingDown className="w-4 h-4 text-secondary" />;
    return <CheckCircle className="w-4 h-4 text-secondary" />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-card border-none rounded-2xl p-0 max-h-[90vh] flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Budget Tracker
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {bucketItem ? `Track expenses for "${bucketItem.title}"` : "Manage your trip budgets"}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-6 border-b border-charcoal/5">
            <TabsList className="bg-transparent gap-4 h-12">
              <TabsTrigger 
                value="overview"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-4"
              >
                <PieChart className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              {bucketItem && (
                <TabsTrigger 
                  value="item"
                  className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-4"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Trip Budget
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <ScrollArea className="flex-1 max-h-[calc(90vh-140px)]">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading budget data...</div>
            ) : isPremiumLocked ? (
              /* Premium Locked State */
              <div className="p-8">
                <div className="relative">
                  {/* Blurred teaser */}
                  <div className="blur-sm select-none pointer-events-none">
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-4 rounded-xl bg-background">
                          <div className="h-3 w-1/2 bg-muted rounded mb-2" />
                          <div className="h-6 w-3/4 bg-muted rounded" />
                        </div>
                      ))}
                    </div>
                    <div className="bg-background rounded-xl p-6">
                      <div className="h-4 w-1/3 bg-muted rounded mb-4" />
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="mb-4">
                          <div className="h-3 w-full bg-muted rounded mb-2" />
                          <div className="h-2 w-2/3 bg-muted rounded" />
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Premium overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-card via-card/95 to-card/80 rounded-xl">
                    <div className="text-center p-6">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                        <DollarSign className="w-8 h-8 text-green-500" />
                      </div>
                      
                      <h3 className="font-serif text-lg font-semibold mb-2">
                        Budget Tracker
                      </h3>
                      
                      <p className="text-sm text-muted-foreground mb-4 max-w-[250px]">
                        Track expenses, set budgets, and manage your trip finances
                      </p>
                      
                      <Button
                        onClick={() => setShowPaywall(true)}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:opacity-90 rounded-full px-6"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Unlock Budget Tracker
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                <TabsContent value="overview" className="p-6 m-0">
                  {summary && (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 rounded-xl bg-background">
                          <p className="text-sm text-muted-foreground mb-1">Total Estimated</p>
                          <p className="text-2xl font-medium text-charcoal">
                            {formatCurrency(summary.total_estimated)}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl bg-background">
                          <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                          <p className="text-2xl font-medium text-charcoal">
                            {formatCurrency(summary.total_spent)}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl bg-background">
                          <p className="text-sm text-muted-foreground mb-1">Variance</p>
                          <p className={`text-2xl font-medium flex items-center gap-2 ${getVarianceColor(summary.total_variance)}`}>
                            {getVarianceIcon(summary.total_variance)}
                            {formatCurrency(Math.abs(summary.total_variance))}
                          </p>
                        </div>
                      </div>

                      {/* By Category */}
                      <div className="bg-background rounded-xl p-6">
                        <h3 className="font-medium text-charcoal mb-4">Budget by Category</h3>
                        <div className="space-y-4">
                          {Object.entries(summary.by_category || {}).map(([cat, data]) => {
                            const percent = data.estimated > 0 ? Math.min(100, (data.spent / data.estimated) * 100) : 0;
                            const isOverBudget = data.spent > data.estimated && data.estimated > 0;
                            
                            return (
                              <div key={cat}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm text-charcoal capitalize">{cat}</span>
                                  <span className="text-sm text-muted-foreground">
                                    {formatCurrency(data.spent)} / {formatCurrency(data.estimated)}
                                  </span>
                                </div>
                                <div className="relative">
                                  <Progress 
                                    value={Math.min(100, percent)} 
                                    className={`h-2 ${isOverBudget ? "[&>div]:bg-destructive" : ""}`}
                                  />
                                  {isOverBudget && (
                                    <AlertTriangle className="absolute -right-1 -top-1 w-4 h-4 text-destructive" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Trips with Budgets */}
                      {summary.trips_with_budget?.length > 0 && (
                        <div className="bg-background rounded-xl p-6">
                          <h3 className="font-medium text-charcoal mb-4">Trip Budgets</h3>
                          <div className="space-y-3">
                            {summary.trips_with_budget.map((trip) => (
                              <div 
                                key={trip.id}
                                className="flex items-center justify-between p-3 rounded-lg bg-card"
                              >
                                <div className="flex items-center gap-3">
                                  {trip.completed ? (
                                    <CheckCircle className="w-5 h-5 text-secondary" />
                                  ) : (
                                    <Target className="w-5 h-5 text-primary" />
                                  )}
                                  <div>
                                    <p className="font-medium text-charcoal text-sm">{trip.title}</p>
                                    <p className="text-xs text-muted-foreground capitalize">{trip.category}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium text-charcoal text-sm">
                                    {formatCurrency(trip.spent)} / {formatCurrency(trip.estimated)}
                                  </p>
                                  {trip.variance !== null && (
                                    <p className={`text-xs flex items-center justify-end gap-1 ${getVarianceColor(trip.variance)}`}>
                                      {trip.variance > 0 ? "+" : ""}{formatCurrency(trip.variance)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Item Budget Tab */}
                {bucketItem && (
                  <TabsContent value="item" className="p-6 m-0">
                    {itemBudget && (
                      <div className="space-y-6">
                        {/* Item Summary */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 rounded-xl bg-background">
                            <p className="text-sm text-muted-foreground mb-1">Estimated</p>
                            <p className="text-xl font-medium text-charcoal">
                              {formatCurrency(itemBudget.estimated_budget)}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-background">
                            <p className="text-sm text-muted-foreground mb-1">Spent</p>
                            <p className="text-xl font-medium text-charcoal">
                              {formatCurrency(itemBudget.actual_spent)}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-background">
                            <p className="text-sm text-muted-foreground mb-1">Remaining</p>
                            <p className={`text-xl font-medium ${itemBudget.variance > 0 ? "text-destructive" : "text-secondary"}`}>
                              {formatCurrency(Math.abs((itemBudget.estimated_budget || 0) - (itemBudget.actual_spent || 0)))}
                              {itemBudget.variance > 0 ? " over" : " left"}
                            </p>
                          </div>
                        </div>

                        {/* Expense by Category */}
                        {Object.keys(itemBudget.expense_by_category || {}).length > 0 && (
                          <div className="bg-background rounded-xl p-6">
                            <h3 className="font-medium text-charcoal mb-4">Spending by Category</h3>
                            <div className="flex flex-wrap gap-3">
                              {EXPENSE_CATEGORIES.map((cat) => {
                                const amount = itemBudget.expense_by_category?.[cat.value] || 0;
                                if (amount === 0) return null;
                                const Icon = cat.icon;
                                
                                return (
                                  <div 
                                    key={cat.value}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card"
                                  >
                                    <div className={`w-8 h-8 rounded-full ${cat.color} bg-opacity-20 flex items-center justify-center`}>
                                      <Icon className="w-4 h-4" style={{ color: cat.color.replace("bg-", "") }} />
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">{cat.label}</p>
                                      <p className="font-medium text-charcoal">{formatCurrency(amount)}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Add Expense Button */}
                        <Button
                          onClick={() => setShowAddExpense(true)}
                          className="w-full bg-primary text-white hover:bg-primary/90"
                          data-testid="add-expense-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Expense
                        </Button>

                        {/* Add Expense Form */}
                        {showAddExpense && (
                          <form onSubmit={addExpense} className="bg-background rounded-xl p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Category</Label>
                                <Select
                                  value={expenseForm.category}
                                  onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {EXPENSE_CATEGORIES.map((cat) => (
                                      <SelectItem key={cat.value} value={cat.value}>
                                        {cat.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Amount (USD)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={expenseForm.amount}
                                  onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                                  data-testid="expense-amount"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Description (optional)</Label>
                              <Input
                                placeholder="What was this for?"
                                value={expenseForm.description}
                                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Date</Label>
                              <Input
                                type="date"
                                value={expenseForm.date}
                                onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button type="submit" className="flex-1 bg-secondary text-white hover:bg-secondary/90">
                                Save Expense
                              </Button>
                              <Button 
                                type="button" 
                                variant="outline"
                                onClick={() => setShowAddExpense(false)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </form>
                        )}

                        {/* Expense List */}
                        {itemBudget.expenses?.length > 0 && (
                          <div className="bg-background rounded-xl p-6">
                            <h3 className="font-medium text-charcoal mb-4">Expense History</h3>
                            <div className="space-y-2">
                              {itemBudget.expenses.map((expense) => {
                                const cat = EXPENSE_CATEGORIES.find(c => c.value === expense.category) || EXPENSE_CATEGORIES[5];
                                const Icon = cat.icon;
                                
                                return (
                                  <div 
                                    key={expense.id}
                                    className="flex items-center justify-between p-3 rounded-lg bg-card"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-full ${cat.color} bg-opacity-20 flex items-center justify-center`}>
                                        <Icon className="w-5 h-5" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-charcoal text-sm">
                                          {expense.description || cat.label}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {expense.date ? format(new Date(expense.date), "MMM d, yyyy") : ""}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <p className="font-medium text-charcoal">
                                        {formatCurrency(expense.amount)}
                                      </p>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => deleteExpense(expense.id)}
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>
                )}
              </>
            )}
          </ScrollArea>
        </Tabs>
      </DialogContent>
      
      {/* Premium Paywall */}
      <PaywallScreen
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        type={PaywallType.BUDGET_TRACKER}
        triggerSource="budget_tracker"
      />
    </Dialog>
  );
};

export default BudgetTracker;
